/* eslint-disable camelcase */
import { Button, Row, useButtonConfirmDialog, useForm } from '@rocketmakers/armstrong';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { FilterSelect } from '../../../../shared/components/filter-select';
import { enumToArray } from '../../../../shared/helpers/enumHelpers';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { InputValidator } from '../../../../shared/hooks/ValidationTypes';
import { BackButton } from '../../components/buttons/back-button';
import { SelectZone } from '../../components/select-zone';
import { useBeeConfig } from '../../hooks/beeConfig';
import { useZones } from '../../hooks/zones';
import { relays } from '../../../../shared/helpers/configurationOptionGenerator';
import { SelectAvailableRelay } from '../../components/select-available-relay';
import './configure-boiler-heating-zone.scss';
import { Spinner } from '../../components/spinner/spinner';
import { DeviceType, HeatingZoneID } from '../../../../shared/api-clients/_api';
import { useConfigureBoilerHeatingZone } from './useConfigureBoilerHeatingZone';
import { makeIdForDevice } from './utils';

interface IHeatingZoneForm {
  heatingZoneID: HeatingZoneID;
  temperatureDial: string;
  temperatureSensor: string;
  control: number;
}

const initialState: IHeatingZoneForm = {
  heatingZoneID: null,
  temperatureDial: null,
  temperatureSensor: null,
  control: null,
};

export const validators: InputValidator<IHeatingZoneForm>[] = [
  {
    key: 'heatingZoneID',
    validators: [
      {
        validate: value => enumToArray(HeatingZoneID).includes(value),
        message: 'Must be a valid heating zone',
      },
    ],
  },
  {
    key: 'temperatureDial',
    validators: [
      {
        validate: value => value?.split(' ').includes(DeviceType.WRF06),
        message: 'Must be a valid temperature dial',
      },
    ],
  },
  {
    key: 'temperatureSensor',
    validators: [
      {
        validate: value =>
          !![DeviceType.WRF04, DeviceType.WRF06, DeviceType.NOVOS3, DeviceType.REGIN].find(deviceType =>
            value?.split(' ').includes(deviceType)
          ),
        message: 'Must be a valid temperature sensor',
      },
    ],
  },
  {
    key: 'control',
    validators: [
      {
        validate: value => value && relays.includes(+value),
        message: 'Must be a valid control',
      },
    ],
  },
];

export const ConfigureBoilerHeatingZoneView: React.FunctionComponent = () => {
  const { heatingZone } = useParams<{ heatingZone: HeatingZoneID; boilerType: string }>();
  const { loading } = useBeeConfig();
  const { getZoneDescription } = useZones();

  const {
    currentDevices,
    handleDelete,
    handleSave,
    temperatureDialOptions,
    temperatureSensorOptions,
    assignedZones,
    assignedDials,
    assignedSensors,
  } = useConfigureBoilerHeatingZone(getZoneDescription);

  const initState: IHeatingZoneForm = React.useMemo(() => {
    return currentDevices
      ? {
          heatingZoneID: heatingZone,
          temperatureDial: currentDevices.tempDial && makeIdForDevice(currentDevices.tempDial),
          temperatureSensor: currentDevices.tempSensor && makeIdForDevice(currentDevices.tempSensor),
          control: currentDevices.relay.params.address,
        }
      : initialState;
  }, [currentDevices]);

  const { DataForm, bind, dataBinder } = useForm(initState);
  const { setTouched, formIsValid, validationErrors } = useValidatedForm<IHeatingZoneForm>(validators, dataBinder);

  const supportsMultiZone = assignedZones.length > 0 && !assignedZones.includes(HeatingZoneID.WHOLE_HOME);

  const deletable = useButtonConfirmDialog({
    content: `Are you sure you want to delete the ${getZoneDescription(heatingZone)} Heating Zone?`,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />
      <h1>
        Configuration <span className="color-primary">Boiler Heating Zone</span>
      </h1>
      <div>
        <SelectZone
          {...bind.select('heatingZoneID')}
          label="Where is the heating zone?"
          filterOptions={supportsMultiZone ? [...assignedZones, HeatingZoneID.WHOLE_HOME] : assignedZones}
          onBlur={setTouched}
          data-qa="select-heating-zone-type"
        />
      </div>
      <div>
        <FilterSelect
          {...bind.select('temperatureDial')}
          label="Which temperature dial is in this zone?"
          filterOptions={assignedDials}
          options={temperatureDialOptions}
          onBlur={setTouched}
          data-qa="select-temperature-dial-type"
        />
      </div>
      <div>
        <FilterSelect
          {...bind.select('temperatureSensor')}
          label="Which temperature sensor is used for this zone?"
          filterOptions={assignedSensors}
          options={temperatureSensorOptions}
          onBlur={setTouched}
          data-qa="select-temperature-sensor-type"
        />
      </div>
      <div>
        <SelectAvailableRelay
          {...bind.select('control')}
          label="What is the control for this zone?"
          onBlur={setTouched}
          data-qa="select-control-type"
          initialValue={initState.control}
        />
      </div>
      <Row className="btn-row">
        {heatingZone && (
          <Button
            className="btn-full secondary-button"
            onClick={async () => {
              if (await deletable()) {
                handleDelete();
              }
            }}
            data-qa="delete-button"
          >
            Delete Heating Zone
          </Button>
        )}
        <Button
          className="continue-button btn-full btn-large"
          onClick={() => handleSave(dataBinder)}
          disabled={!formIsValid}
          data-qa="continue-button"
        >
          {heatingZone ? 'Save Changes' : 'Save'}
        </Button>
      </Row>
    </DataForm>
  );
};
